// Phase 35d — AI Insight panel for the multi-property Compare page.
//
// Renders a one-paragraph LLM-phrased narrative comparison of the
// currently-selected deals at the TOP of the Compare page, above the
// raw metrics table. Includes:
//   • A green "Winner" pill highlighting the algorithmically-strongest deal
//   • Winner reasons + loser reasons in two columns
//   • Refresh button that re-fetches /api/compare/narrative (cache-bust)
//   • "Ask Copilot" CTA that deep-links to Copilot with the compare context
//     pre-loaded as an opener message
//
// Soft-degrades when:
//   • <2 deals selected — renders nothing
//   • LLM call fails — backend returns a deterministic narrative; we render it
//     with a small "fallback" badge
//
// TestIDs: ai-insight-panel, ai-insight-refresh, ai-insight-ask-copilot,
//          ai-insight-narrative, ai-insight-winner, ai-insight-reason-*

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import {
  Sparkles, RefreshCw, MessageCircle, Trophy, Loader2,
  ThumbsUp, AlertTriangle, History, X, ArrowRight,
} from "lucide-react";

export default function AIInsightPanel({ dealIds, dealsByID }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Stable join so the effect doesn't refire on identical sets.
  const idsKey = (dealIds || []).slice().sort().join(",");

  const fetchNarrative = useCallback(async () => {
    if (!dealIds || dealIds.length < 2 || dealIds.length > 5) return;
    setLoading(true);
    setError(null);
    try {
      const { data: payload } = await api.get(
        `/compare/narrative?deals=${encodeURIComponent(dealIds.join(","))}`,
      );
      setData(payload);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to generate insight");
      setData(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);

  const openHistory = useCallback(async () => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const { data: payload } = await api.get(
        `/compare/narrative/history?deals=${encodeURIComponent((dealIds || []).join(","))}&limit=5`,
      );
      setHistory(payload?.rows || []);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [dealIds]);

  useEffect(() => {
    fetchNarrative();
  }, [fetchNarrative]);

  // Don't render the panel when the page hasn't selected enough deals.
  if (!dealIds || dealIds.length < 2 || dealIds.length > 5) return null;

  const handleAskCopilot = async () => {
    // Open Copilot with a pre-filled message contextualising the compare.
    try {
      // 1) Create a fresh conversation (no deal_id — this is multi-deal).
      const { data: conv } = await api.post("/copilot/conversations", {
        deal_id: null,
        title: "Compare insight",
      });
      // 2) Stash the opener message in sessionStorage so Copilot.jsx picks it
      //    up on mount (no auto-send — user can edit first).
      const opener = (
        `I'm comparing these deals: ${dealIds.join(", ")}. ` +
        `Here's what the AI Insight panel showed me:\n\n` +
        `"${data?.narrative || ""}"\n\n` +
        `Use compare_narrative to dig deeper.`
      );
      sessionStorage.setItem(`copilot:opener:${conv.id}`, opener);
      navigate(`/copilot?conv=${conv.id}`);
    } catch {
      // Soft fallback — open Copilot empty.
      navigate("/copilot");
    }
  };

  const winnerMeta = data?.winner_id ? dealsByID?.[data.winner_id] : null;
  const winnerAddress = winnerMeta?.property?.address || data?.winner_id?.slice(0, 8);
  const isFallback = data?.source === "fallback_deterministic"
                  || data?.source === "deterministic_no_llm";
  const delta = data?.delta;
  const hasDelta = delta?.has_prior && (delta.winner_flipped || (delta.changed_metrics?.length ?? 0) > 0);

  return (
    <section
      data-testid="ai-insight-panel"
      className="mt-6 overflow-hidden rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50/40 to-violet-50/30 shadow-sm"
    >
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-indigo-100 bg-white/60 px-4 py-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-indigo-600 text-white">
            <Sparkles className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-slate-900">AI Insight</h3>
            <p className="text-[11px] text-slate-500">
              {loading ? "Generating narrative…" : "One-paragraph comparison by Claude"}
              {isFallback && !loading && (
                <span className="ml-1 inline-flex items-center gap-0.5 rounded bg-amber-100 px-1 py-0.5 text-[10px] font-medium text-amber-700">
                  <AlertTriangle className="h-2.5 w-2.5" /> fallback
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={fetchNarrative}
            disabled={loading}
            data-testid="ai-insight-refresh"
            className="border-indigo-200 text-indigo-700 hover:bg-indigo-100"
          >
            {loading ? (
              <Loader2 className="me-1.5 h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="me-1.5 h-3.5 w-3.5" />
            )}
            Refresh
          </Button>
          <Button
            size="sm"
            onClick={handleAskCopilot}
            disabled={!data || loading}
            data-testid="ai-insight-ask-copilot"
            className="bg-indigo-600 text-white hover:bg-indigo-700"
          >
            <MessageCircle className="me-1.5 h-3.5 w-3.5" />
            Ask Copilot
          </Button>
        </div>
      </header>

      <div className="px-4 py-4">
        {loading && !data ? (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Asking Claude to phrase a comparison…
          </div>
        ) : error ? (
          <p className="text-sm text-red-700" data-testid="ai-insight-error">
            <AlertTriangle className="me-1 inline h-3.5 w-3.5" />
            {error}
          </p>
        ) : data ? (
          <>
            {data.winner_id && (
              <div
                className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-medium text-emerald-700"
                data-testid="ai-insight-winner"
              >
                <Trophy className="h-3 w-3" />
                Winner: <span className="font-semibold">{winnerAddress}</span>
              </div>
            )}
            {hasDelta && (
              <button
                type="button"
                onClick={openHistory}
                data-testid="compare-ai-delta-badge"
                className="mb-3 ml-2 inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-800 transition-all hover:bg-amber-100 hover:shadow-sm"
                title="Tap to see what changed"
              >
                <History className="h-3 w-3" />
                {delta.summary}
                {typeof delta.days_since_prior === "number" && (
                  <span className="ml-1 rounded bg-amber-200 px-1 py-0 text-[10px] text-amber-900">
                    {delta.days_since_prior === 0
                      ? "today"
                      : `${delta.days_since_prior}d ago`}
                  </span>
                )}
              </button>
            )}
            <p
              data-testid="ai-insight-narrative"
              className="text-sm leading-relaxed text-slate-800"
            >
              {data.narrative}
            </p>

            {(data.winner_reasons?.length > 0 || data.loser_reasons?.length > 0) && (
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {data.winner_reasons?.length > 0 && (
                  <div className="rounded-lg border border-emerald-200 bg-white/60 p-3">
                    <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                      <ThumbsUp className="h-3 w-3" />
                      Winner reasons
                    </p>
                    <ul className="space-y-1">
                      {data.winner_reasons.map((r, i) => (
                        <li
                          key={i}
                          className="flex gap-2 text-xs text-slate-700"
                          data-testid={`ai-insight-reason-winner-${i}`}
                        >
                          <span className="text-emerald-500">·</span>
                          {r}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {data.loser_reasons?.length > 0 && (
                  <div className="rounded-lg border border-amber-200 bg-white/60 p-3">
                    <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-amber-700">
                      <AlertTriangle className="h-3 w-3" />
                      Why others fell short
                    </p>
                    <ul className="space-y-1">
                      {data.loser_reasons.map((r, i) => (
                        <li
                          key={i}
                          className="flex gap-2 text-xs text-slate-700"
                          data-testid={`ai-insight-reason-loser-${i}`}
                        >
                          <span className="text-amber-500">·</span>
                          {r}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </>
        ) : null}
      </div>

      {/* Phase 35e — history slideover (right-side panel) */}
      {historyOpen && (
        <div
          data-testid="compare-ai-delta-slideover"
          className="fixed inset-0 z-50 flex justify-end"
        >
          <div
            className="absolute inset-0 bg-slate-900/40"
            onClick={() => setHistoryOpen(false)}
          />
          <aside className="relative h-full w-full max-w-md overflow-y-auto bg-white shadow-2xl">
            <header className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur">
              <h4 className="text-sm font-semibold text-slate-900">
                Insight history
              </h4>
              <button
                type="button"
                onClick={() => setHistoryOpen(false)}
                aria-label="Close"
                className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              >
                <X className="h-4 w-4" />
              </button>
            </header>
            <div className="p-4">
              {historyLoading ? (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Loading prior insights…
                </div>
              ) : history.length === 0 ? (
                <p className="text-sm text-slate-500">
                  No prior insights for this comparison set yet.
                </p>
              ) : (
                <>
                  {/* Delta summary table — was vs now */}
                  {hasDelta && delta.changed_metrics.length > 0 && (
                    <div
                      className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3"
                      data-testid="compare-ai-delta-table"
                    >
                      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-amber-700">
                        What changed
                      </p>
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-amber-200 text-slate-500">
                            <th className="py-1 text-left font-medium">Deal</th>
                            <th className="py-1 text-left font-medium">Metric</th>
                            <th className="py-1 text-right font-medium">Was</th>
                            <th className="py-1 text-right font-medium">Now</th>
                            <th className="py-1 text-right font-medium">Δ</th>
                          </tr>
                        </thead>
                        <tbody>
                          {delta.changed_metrics.map((c, i) => (
                            <tr key={i} className="border-b border-amber-100 last:border-0">
                              <td className="py-1 text-slate-800">
                                {dealsByID?.[c.deal_id]?.property?.address
                                  ? dealsByID[c.deal_id].property.address.split(",")[0]
                                  : c.deal_id.slice(0, 8)}
                              </td>
                              <td className="py-1 text-slate-700">
                                {c.metric.replace(/_/g, " ")}
                              </td>
                              <td className="py-1 text-right text-slate-700 tabular-nums">
                                {typeof c.prev === "number" ? c.prev.toLocaleString() : "—"}
                              </td>
                              <td className="py-1 text-right text-slate-900 tabular-nums">
                                {typeof c.current === "number" ? c.current.toLocaleString() : "—"}
                              </td>
                              <td
                                className={`py-1 text-right font-semibold tabular-nums ${
                                  c.delta_pct >= 0 ? "text-emerald-700" : "text-red-700"
                                }`}
                              >
                                {c.delta_pct > 0 ? "+" : ""}
                                {c.delta_pct}%
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {/* Past narratives, newest first */}
                  <ul className="space-y-3">
                    {history.map((row) => (
                      <li
                        key={row.id || row.generated_at}
                        className="rounded-lg border border-slate-200 bg-white p-3"
                      >
                        <div className="mb-1 flex items-center justify-between text-[10px] text-slate-400">
                          <span>{(row.generated_at || "").slice(0, 10)}</span>
                          {row.winner_id && (
                            <span className="inline-flex items-center gap-0.5">
                              <ArrowRight className="h-2.5 w-2.5" />
                              {dealsByID?.[row.winner_id]?.property?.address?.split(",")[0]
                                || row.winner_id.slice(0, 8)}
                            </span>
                          )}
                        </div>
                        <p className="text-xs leading-relaxed text-slate-700">
                          {row.narrative}
                        </p>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </aside>
        </div>
      )}
    </section>
  );
}
